// Phase 25 iter-5d — manage active roles (ADR-014 multi-role).
//
// Mostra os 4 roles autorrexistrables. Para cada un:
//   • Se está activo: badge "Activo" + "Establecer como principal" (se non
//     é primary) + "Eliminar".
//   • Se non está activo: "Engadir rol".
//
// Engadir un rol que require KYC distinto redirixe a /marketplace/kyc.

import { Check, Search, ShieldCheck, Sprout, Star, Stethoscope, Trash2, Truck } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';

import type { RolesResponse, UserRole } from '@leyrochain/shared';

import { addMyRole, listMyRoles, removeMyRole, setMyPrimaryRole } from '../api/roles';
import { fetchKycStatus } from '../api/kyc';
import { describeAxiosError } from '../api/errors';
import ContentPage from '../components/ui/ContentPage';
import Spinner from '../components/ui/Spinner';
import { useAuthContext } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';

type SelfRole = 'FARMER' | 'BUYER' | 'VET' | 'TRANSPORTISTA';

interface RoleOption {
  id: SelfRole;
  title: string;
  description: string;
  icon: LucideIcon;
  kycRequired: boolean;
}

const ROLE_OPTIONS: RoleOption[] = [
  {
    id: 'FARMER',
    title: 'Gandeiro/a',
    description: 'Xestiono unha explotación, rexistro eventos e vendo animais.',
    icon: Sprout,
    kycRequired: false,
  },
  {
    id: 'BUYER',
    title: 'Comprador/a',
    description: 'Compro animais no marketplace ou na subasta.',
    icon: Search,
    kycRequired: true,
  },
  {
    id: 'VET',
    title: 'Veterinario/a',
    description: 'Aplico actos sanitarios e asino eventos.',
    icon: Stethoscope,
    kycRequired: true,
  },
  {
    id: 'TRANSPORTISTA',
    title: 'Transportista',
    description: 'Documento a recollida e entrega de animais.',
    icon: Truck,
    kycRequired: true,
  },
];

export default function RolesPage(): JSX.Element {
  const { accessToken, refreshUser } = useAuthContext();
  const { addToast } = useToast();
  const navigate = useNavigate();
  const [state, setState] = useState<RolesResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [pending, setPending] = useState<UserRole | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!accessToken) return;
    listMyRoles()
      .then(setState)
      .catch((e) => setError(describeAxiosError(e, 'Non se puido cargar os teus roles.')))
      .finally(() => setLoading(false));
  }, [accessToken]);

  if (!accessToken) {
    return <Navigate to="/marketplace/login?next=/marketplace/profile/roles" replace />;
  }

  async function withPending(role: UserRole, fn: () => Promise<RolesResponse>) {
    setPending(role);
    try {
      const next = await fn();
      setState(next);
      await refreshUser();
    } catch (err) {
      addToast(describeAxiosError(err, 'A operación non se puido completar.'), 'error');
    } finally {
      setPending(null);
    }
  }

  async function handleAdd(opt: RoleOption) {
    await withPending(opt.id, () => addMyRole({ role: opt.id }));
    addToast(`Rol "${opt.title}" engadido.`, 'success');
    if (opt.kycRequired) {
      // Phase 25 iter-10 — KYC de identidade é un por usuario. Se xa está
      // verificado, o novo rol queda activo sen máis pasos. Só redirixir
      // cando aínda non se completou.
      const verified = await fetchKycStatus()
        .then((r) => r.status === 'VERIFIED')
        .catch(() => false);
      if (verified) {
        addToast('KYC xa verificado — o rol activase de inmediato.', 'success');
      } else {
        navigate('/marketplace/kyc');
      }
    }
  }

  async function handleSetPrimary(role: UserRole) {
    await withPending(role, () => setMyPrimaryRole(role));
    addToast('Rol principal actualizado.', 'success');
  }

  async function handleRemove(role: UserRole) {
    if (!confirm('Seguro que queres desactivar este rol? Podes volvelo a engadir despois.')) return;
    await withPending(role, () => removeMyRole(role));
    addToast('Rol desactivado.', 'default');
  }

  return (
    <ContentPage
      title="Os meus roles"
      intro="Engade, quita ou cambia o rol principal. Cada rol pode ter os seus propios requisitos de verificación (KYC, sinatura ECDSA…)."
      category="Conta"
    >
      {loading && (
        <div className="flex items-center gap-3 text-sm text-muted">
          <Spinner size={18} /> Cargando roles…
        </div>
      )}
      {error && (
        <div className="rounded-2xl border border-danger bg-red-50 text-danger text-sm p-4 not-prose">
          {error}
        </div>
      )}

      {state && (
        <ul className="space-y-3 not-prose">
          {ROLE_OPTIONS.map((opt) => {
            const assignment = state.roles.find((r) => r.role === opt.id);
            const active = !!assignment && !assignment.deactivated_at;
            const isPrimary = state.primary_role === opt.id;
            const isPending = pending === opt.id;
            const Icon = opt.icon;
            return (
              <li
                key={opt.id}
                className="border border-border rounded-2xl p-5 bg-white flex items-start gap-4"
              >
                <div
                  className={`w-12 h-12 rounded-2xl flex items-center justify-center shrink-0 ${
                    active ? 'bg-rauxa text-white' : 'bg-subtle text-muted'
                  }`}
                >
                  <Icon size={22} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-semibold text-dark text-base">{opt.title}</h3>
                    {isPrimary && (
                      <span className="text-[10px] uppercase tracking-wide font-bold text-rauxa bg-rauxa/10 px-2 py-0.5 rounded-full inline-flex items-center gap-1">
                        <Star size={10} fill="currentColor" />
                        Principal
                      </span>
                    )}
                    {active && !isPrimary && (
                      <span className="text-[10px] uppercase tracking-wide font-bold text-dark bg-subtle px-2 py-0.5 rounded-full inline-flex items-center gap-1">
                        <Check size={10} />
                        Activo
                      </span>
                    )}
                    {opt.kycRequired && (
                      <span className="text-[10px] uppercase tracking-wide font-bold text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full inline-flex items-center gap-1">
                        <ShieldCheck size={10} />
                        KYC
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-muted mt-1 leading-snug">{opt.description}</p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {!active && (
                      <button
                        type="button"
                        onClick={() => void handleAdd(opt)}
                        disabled={isPending}
                        className="btn-primary !py-2 !px-4 text-xs disabled:opacity-60"
                      >
                        {isPending ? <Spinner size={14} className="mx-auto" /> : 'Engadir rol'}
                      </button>
                    )}
                    {active && !isPrimary && (
                      <>
                        <button
                          type="button"
                          onClick={() => void handleSetPrimary(opt.id)}
                          disabled={isPending}
                          className="btn-outline !py-2 !px-4 text-xs disabled:opacity-60"
                        >
                          {isPending ? (
                            <Spinner size={14} className="mx-auto" />
                          ) : (
                            'Establecer como principal'
                          )}
                        </button>
                        <button
                          type="button"
                          onClick={() => void handleRemove(opt.id)}
                          disabled={isPending}
                          className="btn-ghost !py-2 !px-3 text-xs text-danger disabled:opacity-60 inline-flex items-center gap-1"
                        >
                          <Trash2 size={12} />
                          Desactivar
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      <p className="text-xs text-muted mt-6">
        Algúns roles (Comprador/a, Veterinario/a, Transportista) require unha verificación de
        identidade adicional (KYC). Faino dende{' '}
        <Link to="/marketplace/kyc" className="text-rauxa font-semibold hover:underline">
          /marketplace/kyc
        </Link>{' '}
        en canto engadas o rol.
      </p>
    </ContentPage>
  );
}
